import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Users, Flame, UserPlus, CalendarClock, DollarSign, Trophy, Target, TrendingUp,
  AlertTriangle, Phone, Mail, ArrowRight, CheckCircle2, Clock, Activity,
  Workflow, Sparkles, Send, RefreshCw, ListChecks, FileText, Building2,
  Radar, CalendarCheck, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";

type Stage =
  | "new_lead" | "qualified" | "contacted" | "discovery_scheduled"
  | "discovery_completed" | "proposal_sent" | "negotiation" | "won" | "lost";

interface Lead {
  id: string; name: string; company: string | null; email: string | null; phone: string | null;
  source: string | null; segment: string | null; status: Stage;
  lead_score: number; visitor_score: number; services_interested: string[];
  deal_value: number | null; next_follow_up_at: string | null;
  last_activity_at: string; won_at: string | null; created_at: string;
}
interface Task {
  id: string; lead_id: string; task_type: string; title: string;
  due_at: string | null; completed_at: string | null;
}
interface ActivityRow {
  id: string; lead_id: string; activity_type: string; title: string; created_at: string;
}
interface Visitor {
  visitor_id: string; temperature: string; engagement_score: number;
  last_seen: string; country: string | null; city: string | null;
}
interface Appointment { id: string; name: string; slot_start: string; status: string; created_at: string; }

const STAGE_LABEL: Record<Stage, string> = {
  new_lead: "New Lead", qualified: "Qualified", contacted: "Contacted",
  discovery_scheduled: "Discovery", discovery_completed: "Discovery Done",
  proposal_sent: "Proposal", negotiation: "Negotiation", won: "Won", lost: "Lost",
};
const STAGE_COLOR: Record<Stage, string> = {
  new_lead: "hsl(220 14% 55%)", qualified: "hsl(207 68% 55%)", contacted: "hsl(190 70% 50%)",
  discovery_scheduled: "hsl(260 60% 60%)", discovery_completed: "hsl(280 60% 60%)",
  proposal_sent: "hsl(38 90% 55%)", negotiation: "hsl(20 85% 55%)",
  won: "hsl(142 70% 45%)", lost: "hsl(0 65% 50%)",
};
const PIPELINE_ORDER: Stage[] = ["new_lead","qualified","contacted","discovery_scheduled","discovery_completed","proposal_sent","negotiation","won","lost"];

const startOfToday = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const startOfMonth = () => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; };
const fmtMoney = (n: number) => n >= 1000 ? `$${(n/1000).toFixed(1)}k` : `$${n.toFixed(0)}`;
const fmtAgo = (iso: string) => {
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 1) return "just now";
  if (m < 60) return `${Math.floor(m)}m ago`;
  if (m < 1440) return `${Math.floor(m/60)}h ago`;
  return `${Math.floor(m/1440)}d ago`;
};
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const surface = { background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" };
const muted = "hsl(0 0% 55%)";
const fg = "hsl(0 0% 92%)";
const dim = "hsl(0 0% 42%)";

function recommendedAction(l: Lead): string {
  switch (l.status) {
    case "new_lead": return "Qualify & make first contact";
    case "qualified": return "Reach out, call or email today";
    case "contacted": return "Book discovery call";
    case "discovery_scheduled": return "Prepare discovery agenda";
    case "discovery_completed": return "Send proposal";
    case "proposal_sent": return "Follow up on proposal";
    case "negotiation": return "Close, confirm terms";
    case "won": return "Onboard client";
    case "lost": return "Archive & note reason";
  }
}

const KpiCard = ({ icon: Icon, label, value, sub, accent }: {
  icon: React.ElementType;
  label: string; value: string | number; sub?: string; accent?: string;
}) => (
  <div className="rounded-xl border p-4" style={surface}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-wider" style={{ color: dim }}>{label}</span>
      <Icon size={14} style={{ color: accent || "hsl(207 68% 60%)" }} />
    </div>
    <div className="text-2xl font-bold" style={{ color: fg }}>{value}</div>
    {sub && <div className="text-xs mt-1" style={{ color: muted }}>{sub}</div>}
  </div>
);

export default function ExecutiveCommandCenter() {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: ld }, { data: tk }, { data: ac }, { data: vs }, { data: ap }] = await Promise.all([
      supabase.from("leads").select("*").order("last_activity_at", { ascending: false }).limit(500),
      supabase.from("lead_tasks").select("*").order("due_at", { ascending: true, nullsFirst: false }).limit(200),
      supabase.from("lead_activities").select("id,lead_id,activity_type,title,created_at").order("created_at",{ascending:false}).limit(40),
      supabase.from("visitors").select("visitor_id,temperature,engagement_score,last_seen,country,city").order("last_seen",{ascending:false}).limit(200),
      supabase.from("appointments").select("id,name,slot_start,status,created_at").order("slot_start",{ascending:false}).limit(100),
    ]);
    setLeads((ld || []) as Lead[]);
    setTasks((tk || []) as Task[]);
    setActivities((ac || []) as ActivityRow[]);
    setVisitors((vs || []) as Visitor[]);
    setAppointments((ap || []) as Appointment[]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sensitive tables (leads, appointments, etc.) are intentionally excluded from
  // Realtime for PII protection. Poll every 30s while the tab is visible.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 30_000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { window.clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  // ── KPIs ──
  const today = startOfToday().getTime();
  const month = startOfMonth().getTime();

  const visitorsToday = visitors.filter(v => new Date(v.last_seen).getTime() >= today).length;
  const hotVisitors = visitors.filter(v => v.temperature === "hot").length;
  const newLeadsToday = leads.filter(l => new Date(l.created_at).getTime() >= today).length;
  const discoveryScheduled = leads.filter(l => l.status === "discovery_scheduled").length;
  const openPipelineValue = leads
    .filter(l => l.status !== "won" && l.status !== "lost")
    .reduce((s, l) => s + (l.deal_value || 0), 0);
  const wonThisMonth = leads.filter(l => l.status === "won" && l.won_at && new Date(l.won_at).getTime() >= month);
  const revenueWon = wonThisMonth.reduce((s, l) => s + (l.deal_value || 0), 0);
  const totalClosed = leads.filter(l => l.status === "won" || l.status === "lost").length;
  const conversionRate = totalClosed > 0
    ? Math.round((leads.filter(l => l.status === "won").length / totalClosed) * 100) : 0;
  const revenueInfluenced = leads
    .filter(l => l.status !== "lost")
    .reduce((s, l) => s + (l.deal_value || 0), 0);

  // ── Attention Center ──
  const now = Date.now();
  const sevenDays = 7 * 86400000;
  const attention = useMemo(() => {
    const items: { id: string; severity: "high"|"med"|"low"; title: string; sub: string; leadId: string; action: string; phone?: string|null; email?: string|null }[] = [];
    leads.forEach(l => {
      const isHot = l.lead_score >= 60 || (l.visitor_score >= 30 && l.lead_score >= 20);
      const lastAct = new Date(l.last_activity_at).getTime();
      const inactive = now - lastAct;

      if (isHot && (l.status === "new_lead" || l.status === "qualified")) {
        items.push({ id: "hot-noaction-"+l.id, severity:"high", title:"Hot lead, no follow-up", sub: `${l.name}${l.company?" · "+l.company:""} · score ${l.lead_score}`, leadId: l.id, action: "Call now", phone: l.phone, email: l.email });
      }
      if (l.next_follow_up_at && new Date(l.next_follow_up_at).getTime() < now && l.status !== "won" && l.status !== "lost") {
        items.push({ id: "overdue-"+l.id, severity:"high", title:"Overdue follow-up", sub: `${l.name} · due ${fmtAgo(l.next_follow_up_at)}`, leadId: l.id, action: "Reach out", phone: l.phone, email: l.email });
      }
      if (l.status === "discovery_scheduled" && l.next_follow_up_at) {
        const dt = new Date(l.next_follow_up_at);
        if (dt.getTime() >= today && dt.getTime() < today + 86400000) {
          items.push({ id: "disc-"+l.id, severity:"med", title:"Discovery call today", sub: `${l.name} · ${fmtTime(l.next_follow_up_at)}`, leadId: l.id, action: "Prep call", phone: l.phone, email: l.email });
        }
      }
      if (l.status === "proposal_sent" && inactive > 3 * 86400000) {
        items.push({ id: "prop-"+l.id, severity:"med", title:"Proposal awaiting response", sub: `${l.name} · sent ${fmtAgo(l.last_activity_at)}`, leadId: l.id, action: "Follow up", phone: l.phone, email: l.email });
      }
      if (inactive > sevenDays && l.status !== "won" && l.status !== "lost") {
        items.push({ id: "stale-"+l.id, severity:"low", title:"Lead inactive > 7 days", sub: `${l.name} · ${fmtAgo(l.last_activity_at)}`, leadId: l.id, action: "Revive", phone: l.phone, email: l.email });
      }
    });
    return items.sort((a,b) => ({high:0,med:1,low:2}[a.severity] - {high:0,med:1,low:2}[b.severity])).slice(0, 12);
  }, [leads, now, today]);

  // ── Hot Leads ──
  const hotLeads = useMemo(() => {
    return [...leads]
      .filter(l => l.status !== "won" && l.status !== "lost")
      .sort((a, b) => (b.lead_score + b.visitor_score) - (a.lead_score + a.visitor_score))
      .slice(0, 10);
  }, [leads]);

  // ── Pipeline Health ──
  const pipelineCounts = useMemo(() => {
    const c: Record<Stage, number> = { new_lead:0,qualified:0,contacted:0,discovery_scheduled:0,discovery_completed:0,proposal_sent:0,negotiation:0,won:0,lost:0 };
    leads.forEach(l => { c[l.status] = (c[l.status] || 0) + 1; });
    return c;
  }, [leads]);
  const bottleneck = useMemo(() => {
    const open: Stage[] = ["qualified","contacted","discovery_scheduled","discovery_completed","proposal_sent","negotiation"];
    let max: Stage | null = null; let mc = 0;
    open.forEach(s => { if (pipelineCounts[s] > mc) { mc = pipelineCounts[s]; max = s; } });
    return max && mc >= 3 ? max : null;
  }, [pipelineCounts]);

  // ── Attribution ──
  const attribution = useMemo(() => {
    const bySource: Record<string, { total: number; won: number; revenue: number }> = {};
    leads.forEach(l => {
      const k = l.source || "direct";
      if (!bySource[k]) bySource[k] = { total: 0, won: 0, revenue: 0 };
      bySource[k].total += 1;
      if (l.status === "won") { bySource[k].won += 1; bySource[k].revenue += l.deal_value || 0; }
    });
    const sources = Object.entries(bySource).map(([k,v]) => ({ source:k, ...v, conv: v.total ? Math.round((v.won/v.total)*100) : 0 }));
    const services: Record<string, number> = {};
    leads.forEach(l => (l.services_interested || []).forEach(s => { services[s] = (services[s]||0)+1; }));
    return {
      topSources: [...sources].sort((a,b)=>b.total-a.total).slice(0,5),
      topConverting: [...sources].filter(s=>s.won>0).sort((a,b)=>b.conv-a.conv).slice(0,5),
      topServices: Object.entries(services).sort((a,b)=>b[1]-a[1]).slice(0,5),
    };
  }, [leads]);

  const bookingRate = visitors.length
    ? Math.round((appointments.length / visitors.length) * 100) : 0;
  const callRate = visitors.length
    ? Math.round((leads.filter(l => l.source === "phone_click" || l.phone).length / visitors.length) * 100) : 0;

  // ── Tasks ──
  const openTasks = tasks.filter(t => !t.completed_at);
  const overdueTasks = openTasks.filter(t => t.due_at && new Date(t.due_at).getTime() < now);
  const todayTasks = openTasks.filter(t => t.due_at && new Date(t.due_at).getTime() >= today && new Date(t.due_at).getTime() < today + 86400000);
  const upcomingTasks = openTasks.filter(t => t.due_at && new Date(t.due_at).getTime() >= today + 86400000).slice(0,8);

  // ── Hot Visitors (real-time list, not just count) ──
  const hotVisitorsList = useMemo(() => {
    return [...visitors]
      .filter(v => v.temperature === "hot" || v.engagement_score >= 30)
      .sort((a, b) => (b.engagement_score || 0) - (a.engagement_score || 0))
      .slice(0, 8);
  }, [visitors]);

  // ── Uncontacted Leads (new_lead with no outbound contact activity) ──
  const uncontactedLeads = useMemo(() => {
    const contactedIds = new Set(
      activities
        .filter(a => ["email","call","meeting","outbound","sms"].includes(a.activity_type))
        .map(a => a.lead_id)
    );
    return leads
      .filter(l => l.status === "new_lead" && !contactedIds.has(l.id))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 8);
  }, [leads, activities]);

  // ── Booking Activity (last 14 days) ──
  const bookingActivity = useMemo(() => {
    const cutoff = Date.now() - 14 * 86400000;
    return [...appointments]
      .filter(a => new Date(a.slot_start).getTime() >= cutoff || new Date(a.created_at).getTime() >= cutoff)
      .sort((a, b) => new Date(b.slot_start).getTime() - new Date(a.slot_start).getTime())
      .slice(0, 8);
  }, [appointments]);
  const bookingsToday = appointments.filter(a => new Date(a.slot_start).getTime() >= today && new Date(a.slot_start).getTime() < today + 86400000).length;
  const bookings7d = appointments.filter(a => new Date(a.slot_start).getTime() >= Date.now() - 7*86400000).length;

  const completeTask = async (id: string) => {
    const { error } = await supabase.from("lead_tasks").update({ completed_at: new Date().toISOString() }).eq("id", id);
    if (error) toast.error("Could not complete task"); else { toast.success("Task completed"); load(); }
  };
  const rescheduleTask = async (id: string, days: number) => {
    const due = new Date(); due.setDate(due.getDate() + days);
    const { error } = await supabase.from("lead_tasks").update({ due_at: due.toISOString() }).eq("id", id);
    if (error) toast.error("Could not reschedule"); else { toast.success(`Moved to +${days}d`); load(); }
  };

  // ── Daily summary ──
  const summary = useMemo(() => {
    const wonToday = leads.filter(l => l.status === "won" && l.won_at && new Date(l.won_at).getTime() >= today);
    const newToday = leads.filter(l => new Date(l.created_at).getTime() >= today);
    const proposalsToday = activities.filter(a => a.activity_type === "status_change" && a.title.includes("proposal_sent") && new Date(a.created_at).getTime() >= today);
    const risks = leads.filter(l => l.status === "proposal_sent" && (now - new Date(l.last_activity_at).getTime()) > 3*86400000).length;
    const followups = openTasks.filter(t => t.due_at && new Date(t.due_at).getTime() < today + 86400000).length;
    return { wonToday, newToday, proposalsToday, risks, followups };
  }, [leads, activities, openTasks, today, now]);

  const goPipeline = () => {
    const ev = new CustomEvent("admin:navigate", { detail: { tab: "pipeline" } });
    window.dispatchEvent(ev);
  };

  if (loading) {
    return <div className="text-center py-12" style={{ color: muted }}>Loading command center…</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: fg }}>
            <Sparkles size={18} style={{ color: "hsl(207 68% 60%)" }} /> Executive Command Center
          </h2>
          <p className="text-sm" style={{ color: muted }}>What needs your attention right now.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load}
          style={{ borderColor: "hsl(0 0% 20%)", color: muted, background: "transparent" }}>
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Visitors Today" value={visitorsToday} accent="hsl(207 68% 60%)" />
        <KpiCard icon={Flame} label="Hot Visitors" value={hotVisitors} accent="hsl(20 85% 55%)" />
        <KpiCard icon={UserPlus} label="New Leads" value={newLeadsToday} sub="today" accent="hsl(260 60% 65%)" />
        <KpiCard icon={CalendarClock} label="Discovery Calls" value={discoveryScheduled} sub="scheduled" accent="hsl(280 60% 60%)" />
        <KpiCard icon={DollarSign} label="Pipeline Value" value={fmtMoney(openPipelineValue)} sub="open deals" accent="hsl(38 90% 55%)" />
        <KpiCard icon={Trophy} label="Deals Won" value={wonThisMonth.length} sub={`${fmtMoney(revenueWon)} this month`} accent="hsl(142 70% 50%)" />
        <KpiCard icon={Target} label="Conversion Rate" value={`${conversionRate}%`} sub="won / closed" accent="hsl(190 70% 50%)" />
        <KpiCard icon={TrendingUp} label="Revenue Influenced" value={fmtMoney(revenueInfluenced)} sub="active pipeline" accent="hsl(207 68% 60%)" />
      </div>

      {/* Daily summary */}
      <div className="rounded-xl border p-5" style={{ ...surface, background: "linear-gradient(180deg, hsl(207 68% 50% / 0.06), hsl(0 0% 8%))" }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} style={{ color: "hsl(207 68% 60%)" }} />
          <span className="text-sm font-semibold" style={{ color: fg }}>Today's Owner Briefing</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div><div className="text-xs" style={{ color: dim }}>Wins</div><div style={{ color: "hsl(142 70% 55%)" }}>{summary.wonToday.length} closed{summary.wonToday.length>0?` · ${fmtMoney(summary.wonToday.reduce((s,l)=>s+(l.deal_value||0),0))}`:""}</div></div>
          <div><div className="text-xs" style={{ color: dim }}>New Leads</div><div style={{ color: fg }}>{summary.newToday.length}</div></div>
          <div><div className="text-xs" style={{ color: dim }}>Proposals Sent</div><div style={{ color: fg }}>{summary.proposalsToday.length}</div></div>
          <div><div className="text-xs" style={{ color: dim }}>Risks</div><div style={{ color: summary.risks>0?"hsl(20 85% 60%)":fg }}>{summary.risks} stalled</div></div>
          <div><div className="text-xs" style={{ color: dim }}>Follow-ups</div><div style={{ color: summary.followups>0?"hsl(38 90% 60%)":fg }}>{summary.followups} due</div></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attention Center */}
        <section className="rounded-xl border p-5" style={surface}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: fg }}>
              <AlertTriangle size={14} style={{ color: "hsl(20 85% 60%)" }} /> Attention Center
            </h3>
            <span className="text-xs" style={{ color: dim }}>{attention.length} items</span>
          </div>
          {attention.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: muted }}>All clear. Nothing urgent.</p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {attention.map(a => (
                <div key={a.id} className="rounded-lg border p-3" style={{
                  borderColor: a.severity==="high"?"hsl(20 85% 55% / 0.4)": a.severity==="med"?"hsl(38 90% 55% / 0.3)":"hsl(0 0% 18%)",
                  background: a.severity==="high"?"hsl(20 85% 55% / 0.05)":"hsl(0 0% 10%)",
                }}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium" style={{ color: fg }}>{a.title}</div>
                      <div className="text-xs truncate" style={{ color: muted }}>{a.sub}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{
                      background: a.severity==="high"?"hsl(20 85% 55% / 0.15)":a.severity==="med"?"hsl(38 90% 55% / 0.15)":"hsl(0 0% 18%)",
                      color: a.severity==="high"?"hsl(20 85% 70%)":a.severity==="med"?"hsl(38 90% 65%)":muted,
                    }}>{a.severity}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border" style={{ borderColor:"hsl(0 0% 22%)", color: fg }}><Phone size={11}/> Call</a>}
                    {a.email && <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border" style={{ borderColor:"hsl(0 0% 22%)", color: fg }}><Mail size={11}/> Email</a>}
                    <button onClick={goPipeline} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md" style={{ background:"hsl(207 68% 50%)", color:"white" }}>
                      {a.action} <ArrowRight size={11}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Hot Leads */}
        <section className="rounded-xl border p-5" style={surface}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: fg }}>
              <Flame size={14} style={{ color: "hsl(20 85% 60%)" }} /> Hot Leads
            </h3>
            <button onClick={goPipeline} className="text-xs flex items-center gap-1" style={{ color: "hsl(207 68% 60%)" }}>
              Open pipeline <ArrowRight size={11}/>
            </button>
          </div>
          {hotLeads.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: muted }}>No active leads yet.</p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {hotLeads.map(l => (
                <div key={l.id} className="rounded-lg border p-3" style={{ borderColor: "hsl(0 0% 18%)", background: "hsl(0 0% 10%)" }}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: fg }}>{l.name}</div>
                      <div className="text-xs truncate" style={{ color: muted }}>
                        {l.company || ", "} {l.segment ? `· ${l.segment}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-mono" style={{ color: "hsl(20 85% 65%)" }}>{l.lead_score + l.visitor_score}</div>
                      <div className="text-[10px]" style={{ color: dim }}>score</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-[11px] mb-2">
                    <span className="px-2 py-0.5 rounded-full" style={{ background: STAGE_COLOR[l.status]+"22", color: STAGE_COLOR[l.status] }}>{STAGE_LABEL[l.status]}</span>
                    <span style={{ color: dim }}>· {fmtAgo(l.last_activity_at)}</span>
                    {l.services_interested?.[0] && <span style={{ color: muted }}>· {l.services_interested[0]}</span>}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs italic truncate" style={{ color: muted }}>→ {recommendedAction(l)}</div>
                    <div className="flex gap-1">
                      {l.phone && <a href={`tel:${l.phone}`} className="p-1.5 rounded-md border" style={{ borderColor: "hsl(0 0% 22%)", color: fg }}><Phone size={11}/></a>}
                      {l.email && <a href={`mailto:${l.email}`} className="p-1.5 rounded-md border" style={{ borderColor: "hsl(0 0% 22%)", color: fg }}><Mail size={11}/></a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Pipeline Health */}
      <section className="rounded-xl border p-5" style={surface}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: fg }}>
            <Workflow size={14} style={{ color: "hsl(207 68% 60%)" }} /> Pipeline Health
          </h3>
          {bottleneck && (
            <span className="text-xs px-2 py-1 rounded-md" style={{ background: "hsl(20 85% 55% / 0.15)", color: "hsl(20 85% 70%)" }}>
              Bottleneck: {STAGE_LABEL[bottleneck]}
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
          {PIPELINE_ORDER.map(s => {
            const count = pipelineCounts[s] || 0;
            const max = Math.max(1, ...Object.values(pipelineCounts));
            const h = Math.max(8, Math.round((count / max) * 80));
            return (
              <div key={s} className="rounded-lg border p-2 flex flex-col items-center justify-end text-center" style={{
                borderColor: bottleneck===s?"hsl(20 85% 55% / 0.5)":"hsl(0 0% 16%)",
                background: bottleneck===s?"hsl(20 85% 55% / 0.06)":"hsl(0 0% 10%)",
                minHeight: 120,
              }}>
                <div className="text-xl font-bold mb-1" style={{ color: STAGE_COLOR[s] }}>{count}</div>
                <div className="w-full rounded-sm" style={{ height: h, background: STAGE_COLOR[s] + "33", marginBottom: 4 }} />
                <div className="text-[10px] leading-tight" style={{ color: muted }}>{STAGE_LABEL[s]}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Hot Visitors · Uncontacted Leads · Booking Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hot Visitors */}
        <section className="rounded-xl border p-5" style={surface}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: fg }}>
              <Radar size={14} style={{ color: "hsl(20 85% 60%)" }} /> Hot Visitors
            </h3>
            <span className="text-xs" style={{ color: dim }}>{hotVisitorsList.length} live</span>
          </div>
          {hotVisitorsList.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: muted }}>No hot visitors right now.</p>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {hotVisitorsList.map(v => (
                <div key={v.visitor_id} className="rounded-lg border p-3" style={{ borderColor: "hsl(0 0% 18%)", background: "hsl(0 0% 10%)" }}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: fg }}>
                        {v.city || v.country || "Unknown location"}
                      </div>
                      <div className="text-xs truncate flex items-center gap-1" style={{ color: muted }}>
                        <MapPin size={10}/> {[v.city, v.country].filter(Boolean).join(", ") || ", "}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-mono" style={{ color: "hsl(20 85% 65%)" }}>{v.engagement_score}</div>
                      <div className="text-[10px]" style={{ color: dim }}>{v.temperature}</div>
                    </div>
                  </div>
                  <div className="text-[11px]" style={{ color: dim }}>seen {fmtAgo(v.last_seen)} · {v.visitor_id.slice(0,8)}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Uncontacted Leads */}
        <section className="rounded-xl border p-5" style={surface}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: fg }}>
              <UserPlus size={14} style={{ color: "hsl(260 60% 65%)" }} /> Uncontacted Leads
            </h3>
            <span className="text-xs" style={{ color: dim }}>{uncontactedLeads.length}</span>
          </div>
          {uncontactedLeads.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: muted }}>Every new lead has been contacted. 🎯</p>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {uncontactedLeads.map(l => (
                <div key={l.id} className="rounded-lg border p-3" style={{ borderColor: "hsl(260 60% 65% / 0.3)", background: "hsl(260 60% 65% / 0.04)" }}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: fg }}>{l.name}</div>
                      <div className="text-xs truncate" style={{ color: muted }}>{l.company || l.email || ", "}</div>
                    </div>
                    <div className="text-[10px]" style={{ color: dim }}>{fmtAgo(l.created_at)}</div>
                  </div>
                  <div className="flex gap-1 mt-2">
                    {l.phone && <a href={`tel:${l.phone}`} className="px-2 py-0.5 rounded text-[10px] inline-flex items-center gap-1 border" style={{ borderColor: "hsl(0 0% 22%)", color: fg }}><Phone size={10}/> Call</a>}
                    {l.email && <a href={`mailto:${l.email}`} className="px-2 py-0.5 rounded text-[10px] inline-flex items-center gap-1 border" style={{ borderColor: "hsl(0 0% 22%)", color: fg }}><Mail size={10}/> Email</a>}
                    <button onClick={goPipeline} className="px-2 py-0.5 rounded text-[10px] inline-flex items-center gap-1" style={{ background: "hsl(260 60% 50%)", color: "white" }}>Open <ArrowRight size={10}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Booking Activity */}
        <section className="rounded-xl border p-5" style={surface}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: fg }}>
              <CalendarCheck size={14} style={{ color: "hsl(142 70% 50%)" }} /> Booking Activity
            </h3>
            <span className="text-xs" style={{ color: dim }}>{bookingsToday} today · {bookings7d} / 7d</span>
          </div>
          {bookingActivity.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: muted }}>No recent bookings.</p>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {bookingActivity.map(a => {
                const upcoming = new Date(a.slot_start).getTime() >= Date.now();
                const statusColor = a.status === "confirmed" ? "hsl(142 70% 50%)" : a.status === "cancelled" ? "hsl(0 65% 55%)" : "hsl(38 90% 55%)";
                return (
                  <div key={a.id} className="rounded-lg border p-3" style={{ borderColor: "hsl(0 0% 18%)", background: "hsl(0 0% 10%)" }}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: fg }}>{a.name}</div>
                        <div className="text-xs" style={{ color: muted }}>
                          {new Date(a.slot_start).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0" style={{ background: statusColor + "22", color: statusColor }}>
                        {a.status}
                      </span>
                    </div>
                    <div className="text-[11px]" style={{ color: dim }}>
                      {upcoming ? "upcoming" : "past"} · booked {fmtAgo(a.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">


        {/* Marketing Attribution */}
        <section className="rounded-xl border p-5" style={surface}>
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-4" style={{ color: fg }}>
            <TrendingUp size={14} style={{ color: "hsl(207 68% 60%)" }} /> Marketing Attribution
          </h3>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <div className="uppercase tracking-wider mb-2" style={{ color: dim }}>Top Sources</div>
              <div className="space-y-1">
                {attribution.topSources.length === 0 && <div style={{ color: muted }}>No data yet.</div>}
                {attribution.topSources.map(s => (
                  <div key={s.source} className="flex justify-between" style={{ color: fg }}>
                    <span className="truncate">{s.source}</span><span style={{ color: muted }}>{s.total}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wider mb-2" style={{ color: dim }}>Top Converting</div>
              <div className="space-y-1">
                {attribution.topConverting.length === 0 && <div style={{ color: muted }}>No wins yet.</div>}
                {attribution.topConverting.map(s => (
                  <div key={s.source} className="flex justify-between" style={{ color: fg }}>
                    <span className="truncate">{s.source}</span><span style={{ color: "hsl(142 70% 55%)" }}>{s.conv}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wider mb-2" style={{ color: dim }}>Top Service Interest</div>
              <div className="space-y-1">
                {attribution.topServices.length === 0 && <div style={{ color: muted }}>No data yet.</div>}
                {attribution.topServices.map(([s, n]) => (
                  <div key={s} className="flex justify-between" style={{ color: fg }}>
                    <span className="truncate">{s}</span><span style={{ color: muted }}>{n}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wider mb-2" style={{ color: dim }}>Conversion Rates</div>
              <div className="space-y-1" style={{ color: fg }}>
                <div className="flex justify-between"><span>Booking</span><span style={{ color: muted }}>{bookingRate}%</span></div>
                <div className="flex justify-between"><span>Call</span><span style={{ color: muted }}>{callRate}%</span></div>
                <div className="flex justify-between"><span>Lead → Won</span><span style={{ color: muted }}>{conversionRate}%</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* Activity Stream */}
        <section className="rounded-xl border p-5" style={surface}>
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-4" style={{ color: fg }}>
            <Activity size={14} style={{ color: "hsl(207 68% 60%)" }} /> Live Activity
          </h3>
          {activities.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: muted }}>No activity yet.</p>
          ) : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {activities.slice(0, 20).map(a => (
                <div key={a.id} className="flex items-start gap-2 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: "hsl(207 68% 60%)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate" style={{ color: fg }}>{a.title}</div>
                    <div style={{ color: dim }}>{a.activity_type} · {fmtAgo(a.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Task Command Center */}
      <section className="rounded-xl border p-5" style={surface}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: fg }}>
            <ListChecks size={14} style={{ color: "hsl(207 68% 60%)" }} /> Task Command Center
          </h3>
          <span className="text-xs" style={{ color: dim }}>{openTasks.length} open</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: "Overdue", items: overdueTasks, accent: "hsl(20 85% 60%)" },
            { title: "Today", items: todayTasks, accent: "hsl(38 90% 60%)" },
            { title: "Upcoming", items: upcomingTasks, accent: "hsl(207 68% 60%)" },
          ].map(group => (
            <div key={group.title} className="rounded-lg border p-3" style={{ borderColor: "hsl(0 0% 16%)", background: "hsl(0 0% 10%)" }}>
              <div className="text-xs uppercase tracking-wider mb-2" style={{ color: group.accent }}>{group.title} ({group.items.length})</div>
              {group.items.length === 0 ? (
                <div className="text-xs py-3" style={{ color: muted }}>Nothing here.</div>
              ) : (
                <div className="space-y-2">
                  {group.items.slice(0,6).map(t => (
                    <div key={t.id} className="text-xs rounded-md p-2" style={{ background: "hsl(0 0% 8%)" }}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate" style={{ color: fg }}>{t.title}</div>
                          <div style={{ color: dim }}>{t.task_type}{t.due_at?` · ${fmtAgo(t.due_at)}`:""}</div>
                        </div>
                      </div>
                      <div className="flex gap-1 mt-2">
                        <button onClick={() => completeTask(t.id)} className="px-2 py-0.5 rounded text-[10px] inline-flex items-center gap-1" style={{ background: "hsl(142 70% 45% / 0.15)", color: "hsl(142 70% 60%)" }}>
                          <CheckCircle2 size={10}/> Done
                        </button>
                        <button onClick={() => rescheduleTask(t.id, 1)} className="px-2 py-0.5 rounded text-[10px]" style={{ background: "hsl(0 0% 18%)", color: muted }}>+1d</button>
                        <button onClick={() => rescheduleTask(t.id, 7)} className="px-2 py-0.5 rounded text-[10px]" style={{ background: "hsl(0 0% 18%)", color: muted }}>+7d</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Quick Actions */}
      <section className="rounded-xl border p-5" style={surface}>
        <h3 className="text-sm font-semibold mb-3" style={{ color: fg }}>Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          <a href="tel:+12142962124" className="px-3 py-2 rounded-md border text-xs inline-flex items-center gap-2" style={{ borderColor:"hsl(0 0% 22%)", color: fg }}><Phone size={12}/> Call Lead</a>
          <a href="/book" className="px-3 py-2 rounded-md border text-xs inline-flex items-center gap-2" style={{ borderColor:"hsl(0 0% 22%)", color: fg }}><CalendarClock size={12}/> Book Appointment</a>
          <button onClick={goPipeline} className="px-3 py-2 rounded-md text-xs inline-flex items-center gap-2" style={{ background:"hsl(207 68% 50%)", color:"white" }}><Workflow size={12}/> Open Pipeline</button>
          <button onClick={goPipeline} className="px-3 py-2 rounded-md border text-xs inline-flex items-center gap-2" style={{ borderColor:"hsl(0 0% 22%)", color: fg }}><UserPlus size={12}/> Create Lead</button>
          <button onClick={goPipeline} className="px-3 py-2 rounded-md border text-xs inline-flex items-center gap-2" style={{ borderColor:"hsl(0 0% 22%)", color: fg }}><FileText size={12}/> Add Note</button>
          <button onClick={goPipeline} className="px-3 py-2 rounded-md border text-xs inline-flex items-center gap-2" style={{ borderColor:"hsl(0 0% 22%)", color: fg }}><ListChecks size={12}/> Create Task</button>
        </div>
      </section>
    </div>
  );
}
